"use client";

import { useClerkSync } from "@/hooks/use-clerk-sync";
import { useAuth } from "@clerk/nextjs";
import { Spinner } from "@/components/ui/spinner";
import { useEffect, useState } from "react";

export function ClerkSyncProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const { isTokenReady } = useClerkSync();
  const [isClient, setIsClient] = useState(false);

  // Ensure we only render loading states on the client to prevent hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  // If Clerk is still loading, show nothing (let middleware handle it)
  if (!isLoaded) {
    return <>{children}</>;
  }

  // If user is not signed in, don't show loading - just render children (public pages)
  if (!isSignedIn) {
    return <>{children}</>;
  }

  // During SSR or before client hydration, always render children to prevent mismatch
  if (!isClient) {
    return <>{children}</>;
  }

  // User is signed in but token not ready yet - show loading spinner (client-side only)
  if (!isTokenReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="w-8 h-8" />
          <p className="text-neutral-600 font-medium">Authenticating...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
